import classnames from "classnames";
import React, { useState } from "react";

import { CheckCircle } from "./icons/CheckCircle";
import { ErrorOutline } from "./icons/ErrorOutline";
import { Info } from "./icons/Info";
import { Visibility } from "./icons/Visibility";
import { VisibilityOff } from "./icons/VisibilityOff";

interface InputProps {
  kind: "text" | "password";
  placeholder?: string;
  icon?:
    | { kind: "left"; component: (props: React.SVGProps<SVGSVGElement>) => JSX.Element }
    | { kind: "right"; component: (props: React.SVGProps<SVGSVGElement>) => JSX.Element }
    | { kind: "password-visibility-button" };
  disabled?: boolean;
  value: string;
  onChange: (v: string) => void;
  id?: string;
  label?: string;
  message?:
    | { kind: "warning"; text: string | JSX.Element }
    | { kind: "error"; text: string | JSX.Element }
    | { kind: "success"; text: string | JSX.Element };
  className?: string;
  onFocus?: React.FocusEventHandler<HTMLInputElement>;
  onBlur?: React.FocusEventHandler<HTMLInputElement>;
  autoFocus?: boolean;
}

export function renderInputMessage(m: NonNullable<InputProps["message"]>): JSX.Element {
  const iconClass = classnames("align-middle", "inline", "w-4", "h-4", {
    "text-warning": m.kind === "warning",
    "text-error": m.kind === "error",
    "text-success": m.kind === "success",
  });
  const Icon = m.kind === "warning" ? Info : m.kind === "error" ? ErrorOutline : CheckCircle;
  return (
    <div className="mt-2 txt-field-description">
      <Icon className={iconClass} />
      <span className="ml-2 align-middle text-placeholder">{m.text}</span>
    </div>
  );
}

export interface InputHelperProps {
  kind: "text" | "password";
  disabled?: boolean;
  icon?:
    | { kind: "left"; component: (props: React.SVGProps<SVGSVGElement>) => JSX.Element }
    | { kind: "right"; component: (props: React.SVGProps<SVGSVGElement>) => JSX.Element }
    | { kind: "password-visibility-button" };
  message?: { kind: "warning" } | { kind: "error" } | { kind: "success" };
}

export function getInputHelpers(props: InputHelperProps) {
  const m = props.message;
  const mkind = m?.kind;
  const ikind = props.icon?.kind;
  const icnl = ikind === "left";
  const icnr = ikind === "right" || (props.kind === "password" && ikind === "password-visibility-button");

  const wrapperClass = classnames("flex", "relative", "ring-inset", "focus-within:ring-1", {
    "cursor-not-allowed": props.disabled,
    "bg-background": mkind === undefined,
    "focus-within:ring-body": mkind === undefined,
    "bg-warningbg": mkind === "warning",
    "focus-within:ring-warning": mkind === "warning",
    "bg-errorbg": mkind === "error",
    "focus-within:ring-error": mkind === "error",
    "bg-successbg": mkind === "success",
    "focus-within:ring-success": mkind === "success",
  });
  const inputClass = classnames(
    "py-4",
    "bg-transparent",
    "w-full",
    "txt-field-placeholder",
    "text-ash",
    "placeholder:text-placeholder",
    "border-none",
    "focus:outline-none",
    "focus:ring-0",
    "disabled:pointer-events-none",
    "disabled:text-button-disabled-text",
    "disabled:placeholder:text-button-disabled-text",
    {
      "pl-5": !icnl,
      "pr-5": !icnr,
      "pl-14": icnl,
      "pr-14": icnr,
    }
  );
  const iconWrapperClass = classnames("absolute", "py-4", {
    "left-0 pl-5 pr-3": icnl,
    "right-0 pl-3 pr-5": icnr,
    "cursor-pointer": ikind === "password-visibility-button" && !props.disabled,
    "pointer-events-none": ikind !== "password-visibility-button",
  });
  const iconClass = classnames("w-6", "h-6", {
    "text-button-disabled-text": props.disabled,
  });
  const wrapIcon = (el: JSX.Element, onClick?: () => void) => (
    <div onClick={!props.disabled ? onClick : undefined} className={iconWrapperClass}>
      {el}
    </div>
  );
  return {
    wrapperClass,
    inputClass,
    iconClass,
    wrapIcon,
  };
}

export const Input = React.forwardRef<HTMLDivElement, InputProps>((props, ref) => {
  const m = props.message;
  const [passwordVisible, setPasswordVisible] = useState(false);
  const { wrapperClass, wrapIcon, iconClass, inputClass } = getInputHelpers(props);

  const PasswordButtonIcon = passwordVisible ? Visibility : VisibilityOff;
  const type = props.kind === "password" && passwordVisible ? "text" : props.kind;
  return (
    <div ref={ref} className={props.className}>
      {props.label ? (
        <label className="block text-off-black txt-field-title mb-2" htmlFor={props.id}>
          {props.label}
        </label>
      ) : null}
      <div className={wrapperClass}>
        {props.icon?.kind === "left" ? wrapIcon(props.icon.component({ className: iconClass })) : null}
        <input
          autoFocus={props.autoFocus}
          autoComplete="off"
          id={props.id}
          value={props.value}
          onChange={(e) => {
            props.onChange(e.target.value);
          }}
          disabled={props.disabled}
          className={inputClass}
          placeholder={props.placeholder}
          type={type}
          onFocus={props.onFocus}
          onBlur={props.onBlur}
        />
        {props.icon?.kind === "right" ? wrapIcon(props.icon.component({ className: iconClass })) : null}
        {props.icon?.kind === "password-visibility-button" && props.kind === "password"
          ? wrapIcon(<PasswordButtonIcon className={iconClass} />, () => {
              setPasswordVisible(!passwordVisible);
            })
          : null}
      </div>
      {m ? renderInputMessage(m) : null}
    </div>
  );
});
